import type { Telemetry } from '../ports/telemetry.js';
import { classify } from '../core/result.js';
import type { Unsubscribe } from '../core/disposable.js';

/**
 * Global error boundary.
 *
 * WHY: complements `Result` for the unexpected — uncaught errors and unhandled
 * promise rejections are routed to {@link Telemetry} (never silently lost) and a
 * graceful fallback message replaces the app rather than a blank/broken page.
 *
 * Returns an {@link Unsubscribe} so tests/HMR can detach the handlers.
 */
export function installErrorBoundary(
  telemetry: Telemetry,
  fallback: () => void,
): Unsubscribe {
  const onError = (event: ErrorEvent): void => {
    // We have handled it — suppress the browser's default console escalation.
    event.preventDefault();
    telemetry.error(classify(event.error ?? event.message));
    fallback();
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    event.preventDefault();
    telemetry.error(classify(event.reason));
    fallback();
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
